import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import classnames from 'classnames';

import { FormFieldError, Input, Label } from 'shared/components';
import requestStatuses from 'modules/common/constants/requestsStatuses';
import { checkErrorStatus, isErrorCode } from 'shared/components/reduxFormComponents/checkError';
import { ASTERISK, OPTIONAL, REQUIRED } from 'shared/components/_constants/label';

import theme from './DateOfBirth.scss';

const FULL = 'YEAR_MONTH_DAY';
const YEAR_MONTH = 'YEAR_MONTH';
const YEAR = 'YEAR';

const propTypes = {
  name: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  label: PropTypes.shape({
    id: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired,
  }),
  tooltipType: PropTypes.oneOf(['text', 'hint', 'intl']),
  tooltipTestId: PropTypes.string,
  input: PropTypes.shape({
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.any,
    name: PropTypes.string,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pristine: PropTypes.bool,
    submitFailed: PropTypes.bool,
  }).isRequired,
  errorMessagesMap: PropTypes.object,
  disabled: PropTypes.bool,
  labelSuffix: PropTypes.oneOfType([
    PropTypes.oneOf([ASTERISK, OPTIONAL, REQUIRED]),
    PropTypes.node,
  ]),
  dateFormat: PropTypes.oneOf([FULL, YEAR]),
  dayPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  monthPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  yearPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const defaultProps = {
  name: '',
  label: null,
  onFocus: noop,
  onBlur: noop,
  onChange: noop,
  tooltipType: undefined,
  tooltipTestId: '',
  errorMessagesMap: {},
  disabled: false,
  labelSuffix: null,
  dateFormat: FULL,
  dayPlaceholder: { id: 'Users.UserDetails.BirthDate.Day.Placeholder', defaultMessage: '** dd' },
  monthPlaceholder: { id: 'Users.UserDetails.BirthDate.Month.Placeholder', defaultMessage: '** mm' },
  yearPlaceholder: { id: 'Users.UserDetails.BirthDate.Year.Placeholder', defaultMessage: '** yyyy' },
};

class DateOfBirth extends Component {
  onBlur = () => {
    const [dd = '', mm = '', yyyy = ''] = this.props.input.value.split('/');
    let value = '';
    if (!dd && !mm && !yyyy) value = '';
    else value = `${ dd }/${ mm }/${ yyyy }`;
    this.props.input.onBlur(value);
  };

  getTooltipMessage = () => {
    const { error } = this.props.meta;
    return !isErrorCode(error)
      ? { defaultMessage: error, id: 'default' }
      : error && this.props.errorMessagesMap[error];
  }

  getStatus = () => {
    return checkErrorStatus(this.props.meta)
      ? requestStatuses.failure
      : requestStatuses.default;
  }

  handleFieldChange = (fieldValue, field) => {
    let [dd = '', mm = '', yyyy = ''] = this.props.input.value.split('/');

    switch (field) {
      case 'dd':
        dd = fieldValue;
        break;
      case 'mm':
        mm = fieldValue;
        break;
      case 'yyyy':
        yyyy = fieldValue;
        break;
      default:
        return;
    }

    let value;
    switch (this.props.dateFormat) {
      case FULL:
        value = !dd && !mm && !yyyy ? '' : `${ dd }/${ mm }/${ yyyy }`;
        break;
      case YEAR_MONTH:
        value = !mm && !yyyy ? '' : `1/${ mm }/${ yyyy }`;
        break;
      case YEAR:
        value = !yyyy ? '' : `1/1/${ yyyy }`;
        break;
      default:
        value = '';
    }

    this.props.input.onChange(value);
  };

  render() {
    const {
      label,
      labelSuffix,
      name,
      tooltipType,
      tooltipTestId,
      input,
      disabled,
      dateFormat,
      dayPlaceholder,
      monthPlaceholder,
      yearPlaceholder,
    } = this.props;

    const [dd = '', mm = '', yyyy = ''] = input.value.split('/');
    const status = this.getStatus();

    return (
      <div>
        <If condition={ label }>
          <Label
            label={ label }
            className={ theme.label }
            labelSuffix={ labelSuffix }
          />
        </If>

        <div className={ theme.root } >
          <If condition={ dateFormat === FULL }>
            <div className={ theme.day }>
              <Input
                data-testId="date-of-birth-input-day"
                name={ name }
                testId={ name }
                value={ dd }
                validatePattern="^[0-9]"
                maxLength={ 2 }
                status={ status }
                placeholder={ dayPlaceholder }
                onChange={ (value) => this.handleFieldChange(value, 'dd') }
                onFocus={ input.onFocus }
                onBlur={ this.onBlur }
                disabled={ disabled }
                inputMode="numeric"
              />
            </div>
          </If>

          <If condition={ [YEAR_MONTH, FULL].includes(dateFormat) }>
            <div className={ theme.month }>
              <Input
                data-testId="date-of-birth-input-month"
                name={ name }
                testId={ name }
                value={ mm }
                maxLength={ 2 }
                status={ status }
                validatePattern="^[0-9]"
                placeholder={ monthPlaceholder }
                onChange={ (value) => this.handleFieldChange(value, 'mm') }
                onFocus={ input.onFocus }
                onBlur={ this.onBlur }
                disabled={ disabled }
                inputMode="numeric"
              />
            </div>
          </If>

          <div className={ classnames(theme.year, { [theme.fullWidth]: dateFormat === YEAR }) }>
            <Input
              data-testId="date-of-birth-input-year"
              name={ name }
              testId={ name }
              value={ yyyy }
              maxLength={ 4 }
              status={ status }
              validatePattern="^[0-9]"
              placeholder={ yearPlaceholder }
              onChange={ (value) => this.handleFieldChange(value, 'yyyy') }
              onFocus={ input.onFocus }
              onBlur={ this.onBlur }
              disabled={ disabled }
              inputMode="numeric"
            />
          </div>
        </div>

        <FormFieldError
          tooltipType={ tooltipType }
          testId={ tooltipTestId }
          tooltipMessage={ checkErrorStatus(this.props.meta) ? this.getTooltipMessage() : null }
        />
      </div>
    );
  }
}

DateOfBirth.propTypes = propTypes;
DateOfBirth.defaultProps = defaultProps;

export default DateOfBirth;
